import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
	$getSelection,
	$isRangeSelection,
	COMMAND_PRIORITY_LOW,
	KEY_ARROW_DOWN_COMMAND,
	KEY_ARROW_UP_COMMAND,
	KEY_ENTER_COMMAND,
	KEY_ESCAPE_COMMAND,
	KEY_TAB_COMMAND
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TMentionOption } from '@/types/common';
import MentionDropdown from './mention-dropdown';

interface MentionsPluginProps {
	options: TMentionOption[];
	trigger?: string;
}

const MentionsPlugin = ({ options, trigger = '@' }: MentionsPluginProps) => {
	const [editor] = useLexicalComposerContext();
	const [isOpen, setIsOpen] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const [filteredOptions, setFilteredOptions] = useState<TMentionOption[]>([]);
	const containerRef = useRef<HTMLDivElement>(null);

	const closeDropdown = useCallback(() => {
		setIsOpen(false);
		setSelectedIndex(0);
		setFilteredOptions([]);
	}, []);

	const calculateDropdownPosition = useCallback(() => {
		const domSelection = window.getSelection();
		if (domSelection && domSelection.rangeCount > 0) {
			const range = domSelection.getRangeAt(0);
			const rect = range.getBoundingClientRect();
			const containerRect = containerRef.current?.getBoundingClientRect();

			if (containerRect) {
				setPosition({
					top: rect.bottom - containerRect.top + 5,
					left: rect.left - containerRect.left
				});
			}
		}
	}, []);

	const handleMentionDetection = useCallback(
		(textContent: string, anchorOffset: number) => {
			const atIndex = textContent.lastIndexOf(trigger, anchorOffset - 1);

			if (atIndex === -1) {
				closeDropdown();
				return;
			}

			const textAfterAt = textContent.substring(atIndex + 1, anchorOffset);
			const hasSpaceAfterAt = textAfterAt.includes(' ');
			const hasClosingBrace = textAfterAt.includes('}');

			// Close dropdown if there's a space or closing brace (mention is complete)
			if (hasSpaceAfterAt || hasClosingBrace) {
				closeDropdown();
				return;
			}

			// Check if we're inside a mention with opening brace
			const textBeforeCursor = textContent.substring(0, anchorOffset);
			const lastOpenBrace = textBeforeCursor.lastIndexOf('{');
			const lastAt = textBeforeCursor.lastIndexOf(trigger);

			// Show dropdown if we're after @{ and before }
			if (
				lastAt !== -1 &&
				lastOpenBrace > lastAt &&
				lastOpenBrace < anchorOffset
			) {
				// Extract the query text after the opening brace
				const queryText = textAfterAt.substring(textAfterAt.indexOf('{') + 1);
				const filtered = options.filter((option) =>
					option.value.toLowerCase().includes(queryText.toLowerCase())
				);

				if (filtered.length > 0) {
					setFilteredOptions(filtered);
					setSelectedIndex(0);
					setIsOpen(true);
					calculateDropdownPosition();
				} else {
					closeDropdown();
				}
			} else {
				closeDropdown();
			}
		},
		[options, closeDropdown, calculateDropdownPosition, trigger]
	);

	const insertMention = useCallback(
		(option: TMentionOption) => {
			editor.update(() => {
				const selection = $getSelection();
				if ($isRangeSelection(selection)) {
					// Remove the trigger, opening brace, and query text
					const anchorNode = selection.anchor.getNode();
					const textContent = anchorNode.getTextContent();
					const atIndex = textContent.lastIndexOf(
						trigger,
						selection.anchor.offset - 1
					);

					if (atIndex !== -1) {
						// Find the opening brace after the @
						const textAfterAt = textContent.substring(atIndex + 1);
						const openBraceIndex = textAfterAt.indexOf('{');

						if (openBraceIndex !== -1) {
							const startOfMention = atIndex;
							const endOfMention = selection.anchor.offset;

							// Move selection to the start of the @ symbol
							selection.anchor.set(anchorNode.getKey(), startOfMention, 'text');
							selection.focus.set(anchorNode.getKey(), endOfMention, 'text');

							// Insert as plain text instead of mention node
							selection.insertText(`@{${option.value}} `);
						}
					}
				}
			});
			closeDropdown();
		},
		[editor, closeDropdown, trigger]
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (!isOpen) {
				return false;
			}

			switch (event.key) {
				case 'ArrowDown': {
					event.preventDefault();
					setSelectedIndex((prev) =>
						prev < filteredOptions.length - 1 ? prev + 1 : 0
					);
					return true;
				}
				case 'ArrowUp': {
					event.preventDefault();
					setSelectedIndex((prev) =>
						prev > 0 ? prev - 1 : filteredOptions.length - 1
					);
					return true;
				}
				case 'Enter':
				case 'Tab': {
					event.preventDefault();
					if (filteredOptions[selectedIndex]) {
						insertMention(filteredOptions[selectedIndex]);
					}
					return true;
				}
				case 'Escape': {
					event.preventDefault();
					closeDropdown();
					return true;
				}
				default: {
					// No action needed for other keys
					return false;
				}
			}
		},
		[isOpen, filteredOptions, selectedIndex, insertMention, closeDropdown]
	);

	useEffect(() => {
		return editor.registerCommand(
			KEY_ARROW_DOWN_COMMAND,
			handleKeyDown,
			COMMAND_PRIORITY_LOW
		);
	}, [editor, handleKeyDown]);

	useEffect(() => {
		return editor.registerCommand(
			KEY_ARROW_UP_COMMAND,
			handleKeyDown,
			COMMAND_PRIORITY_LOW
		);
	}, [editor, handleKeyDown]);

	useEffect(() => {
		return editor.registerCommand(
			KEY_ENTER_COMMAND,
			handleKeyDown,
			COMMAND_PRIORITY_LOW
		);
	}, [editor, handleKeyDown]);

	useEffect(() => {
		return editor.registerCommand(
			KEY_TAB_COMMAND,
			handleKeyDown,
			COMMAND_PRIORITY_LOW
		);
	}, [editor, handleKeyDown]);

	useEffect(() => {
		return editor.registerCommand(
			KEY_ESCAPE_COMMAND,
			handleKeyDown,
			COMMAND_PRIORITY_LOW
		);
	}, [editor, handleKeyDown]);

	useEffect(() => {
		const removeUpdateListener = editor.registerUpdateListener(
			({ editorState }) => {
				editorState.read(() => {
					const selection = $getSelection();
					if ($isRangeSelection(selection)) {
						const anchorNode = selection.anchor.getNode();
						const textContent = anchorNode.getTextContent();
						const anchorOffset = selection.anchor.offset;

						handleMentionDetection(textContent, anchorOffset);
					}
				});
			}
		);

		return removeUpdateListener;
	}, [editor, handleMentionDetection]);

	return (
		<div
			ref={containerRef}
			style={{ position: 'relative' }}
		>
			{isOpen && (
				<MentionDropdown
					onClose={closeDropdown}
					onSelect={insertMention}
					options={filteredOptions}
					position={position}
					selectedIndex={selectedIndex}
				/>
			)}
		</div>
	);
};

export default MentionsPlugin;
